import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { AbstractFeatureService } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL } from '@y42/feature/json-schema';
import { GitQuery } from '@y42/git';
import { Y42FeatureType, Y42Metadata } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { QueryFieldWithFunctionTree } from '../../models/widget';
import {
  extractWidgetBuilderModelFromUIModel,
  VisualizationWidgetGitService,
  VisualizationWidgetQuery,
  VisualizationWidgetState,
  VisualizationWidgetStore,
  VisualizationWidgetStoreModel,
  VisualizationWidgetUIModel,
} from '../../state/widget';
import { CreateVisualizationWidgetParams } from '../../state/widget/visualization-widget-create.model';
import { VisualizationWidgetAclService } from './visualization-widget-acl.service';
import { VisualizationWidgetUserDataService } from './visualization-widget-user-data.service';

@Injectable({ providedIn: 'root' })
export class VisualizationWidgetService extends AbstractFeatureService<VisualizationWidgetState> {
  constructor(
    protected readonly query: VisualizationWidgetQuery,
    protected readonly store: VisualizationWidgetStore,
    protected readonly acl: VisualizationWidgetAclService,
    protected readonly git: VisualizationWidgetGitService,
    protected readonly gitQuery: GitQuery,
    protected readonly toast: ToastService,
    readonly userData: VisualizationWidgetUserDataService,
  ) {
    super({
      featureType: Y42FeatureType.VisualizationWidget,
      store,
      query,
      userData,
      git,
      acl,
      gitQuery,
      toast,
      childStores: [],
    });
  }

  async create({ name: widgetName, description, widget: widgetModel }: CreateVisualizationWidgetParams) {
    const { id, name } = this.query.getUniqueIdNamePair(widgetName);

    const asset = {
      ...new Y42Metadata(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name: id,
      ...widgetModel,
      name,
      description,
    };

    if (widgetModel.fields) {
      asset.fields = this.removeFunctionTreeFromFields(widgetModel.fields);
    }

    const widget = this.store.create(asset);

    await this.store.pending;

    this.setLastUsed(widget.$path);

    return widget;
  }

  async duplicate(widget: VisualizationWidgetUIModel) {
    return await this.create({
      name: widget.name,
      description: widget.description ?? '',
      widget: extractWidgetBuilderModelFromUIModel({ ...widget }),
    });
  }

  async update(
    item: Partial<VisualizationWidgetStoreModel>,
    $path: VisualizationWidgetStoreModel['$path'],
  ): Promise<void> {
    this.store.update($path, this.removeFunctionTreeFromSettings(item));
    await this.store.pending;
  }

  async delete($path = this.query.getActiveId() as getIDType<VisualizationWidgetState>): Promise<void> {
    this.store.delete($path);
    await this.store.pending;
  }

  private removeFunctionTreeFromSettings(
    widget: Partial<VisualizationWidgetStoreModel>,
  ): Partial<VisualizationWidgetStoreModel> {
    const fields = widget.fields;

    if (!fields) {
      return widget;
    }

    return {
      ...widget,
      fields: this.removeFunctionTreeFromFields(fields),
    };
  }

  private removeFunctionTreeFromFields(fields: QueryFieldWithFunctionTree[]): QueryFieldWithFunctionTree[] {
    return fields.map((field) => {
      const newField = {
        ...field,
      };
      delete newField.functionTree;
      return newField;
    });
  }
}
